import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import CreditPurchaseButton from '../components/CreditPurchaseButton';
import { getCreditBalance } from '../services/creditsService';
import { getProductById, likeProduct } from '../services/productService';
import { useAuth } from '../contexts/AuthContext';

interface ProductImage {
  id: number;
  url: string;
  alt: string;
}

interface ProductDetail {
  id: number;
  title: string;
  description: string;
  creator: string;
  price: number;
  discountedPrice?: number;
  rating: number;
  reviewCount: number;
  sales: number;
  badges: string[];
  isNew: boolean;
  images: ProductImage[];
  features: string[];
  requirements: {
    software: string;
    system: string[];
    hardware: string[];
  };
  files: {
    name: string;
    size: string;
    type: string;
  }[];
  isLiked: boolean;
}

const ProductPage = () => {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [activeTab, setActiveTab] = useState('description');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const navigate = useNavigate();
  const { isAuthenticated: authContextAuthenticated } = useAuth();

  // Load product data from API
  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) return;

      try {
        const apiProduct = await getProductById(parseInt(productId));

        if (apiProduct) {
          // Transform API product to the format expected by the UI
          const productDetail: ProductDetail = {
            id: apiProduct.id,
            title: apiProduct.title,
            description: apiProduct.description,
            creator: apiProduct.sellerName,
            price: apiProduct.price,
            // Calculate discounted price if applicable
            discountedPrice: apiProduct.price * 0.8, // Example: 20% discount, adjust based on your business logic
            rating: apiProduct.rating,
            reviewCount: 142, // This should come from API in the future
            sales: 850, // This should come from API in the future
            badges: apiProduct.tags,
            isNew: new Date(apiProduct.releaseDate) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Is it newer than 7 days?
            isLiked: false, // This should be determined by user preferences from API
            images: apiProduct.images.map((url, idx) => ({
              id: idx + 1,
              url: url,
              alt: `${apiProduct.title} image ${idx + 1}`
            })),
            features: apiProduct.features,
            // These fields need to be added to your API model or fetched separately
            requirements: {
              software: "Unity 2020.3+, Unreal Engine 5+, or Blender 3.0+",
              system: [
                "Windows 10/11 or macOS 12+",
                "16GB RAM recommended",
                "5GB disk space"
              ],
              hardware: [
                "GPU with DX11 support",
                "CPU with SSE4.2 support"
              ]
            },
            files: [
              { name: `${apiProduct.title.replace(/\s+/g, '')}_Main.fbx`, size: "250MB", type: "3D Model" },
              { name: "Textures_4K.zip", size: "1.2GB", type: "Textures" },
              { name: "Documentation.pdf", size: "5MB", type: "Documentation" },
              { name: "Demo_Scene.unity", size: "150MB", type: "Unity Project" }
            ]
          };

          setProduct(productDetail);
          setIsLiked(productDetail.isLiked);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    fetchProduct();
  }, [productId]);

  // Add an effect to check credit balance
  useEffect(() => {
    // Check if user is authenticated before fetching credit balance
    const fetchCreditBalance = async () => {
      try {
        // Only make the API call if the user is authenticated
        if (authContextAuthenticated) {
          const balance = await getCreditBalance();
          setCreditBalance(balance);
        } else {
          // Set balance to 0 if not authenticated
          setCreditBalance(0);
        }
      } catch (error) {
        console.error('Error fetching credit balance:', error);
        setCreditBalance(0);
      }
    };

    fetchCreditBalance();

    // Check if the interval already exists
    const intervalId = setInterval(() => {
      fetchCreditBalance();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [authContextAuthenticated]);

  // Check if user has enough credits
  const hasEnoughCredits = creditBalance >= (product?.discountedPrice || product?.price || 0);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleLike = async () => {
    if (!product) return;

    setIsLiked(!isLiked);

    try {
      // Update like status on the server
      await likeProduct(product.id);
    } catch (error) {
      console.error('Error updating like status:', error);
      // Revert UI state if API call fails
      setIsLiked(isLiked);
    }
  };

  if (!isLoaded) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <div className="animate-pulse flex flex-col w-full max-w-6xl">
          <div className="h-8 bg-[#2A2633] rounded w-3/4 mb-8"></div>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-1/2 h-96 bg-[#2A2633] rounded-lg"></div>
            <div className="w-full md:w-1/2">
              <div className="h-6 bg-[#2A2633] rounded w-1/4 mb-4"></div>
              <div className="h-10 bg-[#2A2633] rounded w-full mb-6"></div>
              <div className="h-4 bg-[#2A2633] rounded w-full mb-2"></div>
              <div className="h-4 bg-[#2A2633] rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-[#2A2633] rounded w-5/6 mb-6"></div>
              <div className="h-12 bg-[#2A2633] rounded w-1/3 mb-6"></div>
              <div className="h-10 bg-[#2A2633] rounded w-full mb-4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-4">Product not found</h2>
        <p className="text-[#A3A3A3] mb-8">The product you're looking for doesn't exist or has been removed.</p>
        <Button variant="primary" size="lg" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumbs */}
      <div className="text-sm text-[#A3A3A3] mb-6">
        <a href="/" className="hover:text-white">Home</a>
        <span className="mx-2">›</span>
        <a href="/marketplace" className="hover:text-white">Marketplace</a>
        <span className="mx-2">›</span>
        <a href="/3d-models" className="hover:text-white">3D Models</a>
        <span className="mx-2">›</span>
        <span className="text-white">{product.title}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Product Images */}
        <div>
          <div className="rounded-lg overflow-hidden h-[400px] bg-[#1F1A27] mb-4">
            <img
              src={product.images[activeImageIndex].url}
              alt={product.images[activeImageIndex].alt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-cols-4 gap-4">
            {product.images.map((image, index) => (
              <div
                key={image.id}
                className={`h-20 rounded-md overflow-hidden cursor-pointer border-2 ${
                  activeImageIndex === index
                    ? 'border-[#7F56D9]'
                    : 'border-transparent hover:border-[#A3A3A3]/50'
                }`}
                onClick={() => setActiveImageIndex(index)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Product Info */}
        <div>
          {/* Creator info */}
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 rounded-full bg-[#F670C7] mr-3 flex items-center justify-center text-white font-bold">
              {product.creator.charAt(0)}
            </div>
            <div>
              <p className="text-[#A3A3A3] text-sm">Created by</p>
              <p className="font-medium">{product.creator}</p>
            </div>
            <a href="#" className="ml-auto text-[#7F56D9] hover:underline text-sm font-medium">
              View Profile
            </a>
          </div>

          {/* Title and badges */}
          <div className="mb-4">
            <h1 className="text-3xl font-bold mb-3">{product.title}</h1>
            <div className="flex flex-wrap gap-2">
              {product.badges.map((badge, index) => (
                <span
                  key={index}
                  className={`text-xs font-medium py-0.5 px-2 rounded-full ${
                    index === 0 ? 'bg-[#7F56D9] text-white' : 'bg-[#2A2633] text-white'
                  }`}
                >
                  {badge}
                </span>
              ))}
              {product.isNew && (
                <span className="text-xs font-medium py-0.5 px-2 rounded-full bg-[#F04438] text-white">
                  New
                </span>
              )}
            </div>
          </div>

          {/* Ratings and metrics */}
          <div className="flex items-center gap-4 mb-6 text-sm">
            <div className="flex items-center">
              <div className="text-yellow-400 flex items-center mr-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z"/>
                </svg>
              </div>
              <span className="font-medium">{product.rating}</span>
              <span className="text-[#A3A3A3] ml-1">({product.reviewCount} reviews)</span>
            </div>
            <div className="text-[#A3A3A3]">
              {product.sales} sales
            </div>
          </div>

          {/* Price */}
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <div className="flex items-center text-[#F7931A]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="mr-2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {product.discountedPrice && (
                  <span className="text-xl text-[#A3A3A3] line-through mr-2">{Math.round(product.price)}</span>
                )}
                <span className="text-3xl font-bold">{Math.round(product.discountedPrice || product.price)}</span>
              </div>
              {product.discountedPrice && (
                <span className="bg-[#F59E0B] text-white text-xs font-medium py-0.5 px-2 rounded-full">
                  {Math.round((1 - product.discountedPrice / product.price) * 100)}% OFF
                </span>
              )}
            </div>
            <p className="text-[#A3A3A3] text-sm mt-3">
              Includes lifetime updates and 6 months of support
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex gap-4">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => navigate('/cart?add=' + product.id)}
              >
                Add to Cart
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="!px-4 flex-shrink-0"
                onClick={handleLike}
              >
                {isLiked ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="animate-heartbeat">
                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z"
                      fill="#FF3C8E"
                      stroke="#FFAACE" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-gray-400 hover:text-[#FF3C8E]" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" strokeWidth="1.5" />
                  </svg>
                )}
              </Button>
            </div>

            {/* Credit Purchase Option */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-[#7F56D9] mr-2" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-sm text-[#A3A3A3]">Your balance: {creditBalance.toFixed(0)} Credits</span>
                </div>
                <Link to="/topup" className="text-sm text-[#7F56D9] hover:underline">
                  Add Credits
                </Link>
              </div>
              <CreditPurchaseButton
                price={product.discountedPrice || product.price}
                productName={product.title}
                sellerId="seller123"
                showCreditsBalance={false}
                showBuyButton={true}
                className="w-full"
                onSuccess={() => {
                  // Show success message or redirect
                  alert("Purchase successful!");
                }}
              />
              {!hasEnoughCredits && (
                <p className="text-xs text-[#F04438] mt-1">
                  Insufficient credits. <Link to="/topup" className="underline">Add more credits</Link> to purchase this item.
                </p>
              )}

              <div className="mt-4 text-xs text-[#A3A3A3] flex items-center">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-[#F7931A] mr-2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.83 10.94C8.6445 8.744 10.1535 7.832 12.05 7.44L12.05 5.5H13.45L13.45 7.4C13.856 7.4 14.272 7.426 14.69 7.454L14.69 8.83C14.352 8.798 13.994 8.776 13.64 8.776C13.176 8.776 12.735 8.808 12.328 8.844L12.328 12.03C14.642 12.422 16.35 13.034 16.35 15.033C16.35 17.054 14.75 18.084 12.7 18.336L12.7 20.5H11.3L11.3 18.35C10.792 18.35 10.27 18.322 9.75 18.28L9.75 16.88C10.118 16.916 10.54 16.934 10.96 16.934C11.614 16.934 12.17 16.898 12.644 16.844L12.644 13.39C10.384 13.008 8.83 12.318 8.83 10.94ZM12.41 8.956C11.428 9.124 10.78 9.598 10.892 10.838C10.95 11.346 11.382 11.7 12.41 11.976L12.41 8.956ZM13.164 16.77C14.236 16.626 14.9 16.034 14.84 14.998C14.786 14.106 14.222 13.674 13.164 13.408L13.164 16.77Z"/>
                </svg>
                <span>Credits can be converted to Bitcoin at any time.</span>
              </div>
            </div>
          </div>

          {/* Details tabs */}
          <div className="border-b border-[#3A3643] mb-6">
            <div className="flex gap-6">
              <button
                className={`py-3 font-medium text-sm ${
                  activeTab === 'description'
                    ? 'text-white border-b-2 border-[#7F56D9]'
                    : 'text-[#A3A3A3] hover:text-white'
                }`}
                onClick={() => handleTabChange('description')}
              >
                Description
              </button>
              <button
                className={`py-3 font-medium text-sm ${
                  activeTab === 'features'
                    ? 'text-white border-b-2 border-[#7F56D9]'
                    : 'text-[#A3A3A3] hover:text-white'
                }`}
                onClick={() => handleTabChange('features')}
              >
                Features
              </button>
              <button
                className={`py-3 font-medium text-sm ${
                  activeTab === 'requirements'
                    ? 'text-white border-b-2 border-[#7F56D9]'
                    : 'text-[#A3A3A3] hover:text-white'
                }`}
                onClick={() => handleTabChange('requirements')}
              >
                Requirements
              </button>
              <button
                className={`py-3 font-medium text-sm ${
                  activeTab === 'files'
                    ? 'text-white border-b-2 border-[#7F56D9]'
                    : 'text-[#A3A3A3] hover:text-white'
                }`}
                onClick={() => handleTabChange('files')}
              >
                Files
              </button>
            </div>
          </div>

          {/* Tab content */}
          <div className="text-[#A3A3A3] leading-relaxed">
            {activeTab === 'description' && (
              <p>{product.description}</p>
            )}

            {activeTab === 'features' && (
              <ul className="list-disc pl-5 space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            )}

            {activeTab === 'requirements' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-white font-medium mb-2">Software</h3>
                  <p>{product.requirements.software}</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">System</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {product.requirements.system.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">Hardware</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {product.requirements.hardware.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'files' && (
              <div className="space-y-3">
                {product.files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#2A2633] rounded-lg">
                    <div className="flex items-center">
                      <div className="mr-3 p-2 bg-[#3A3643] rounded">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg" className="text-[#7F56D9]">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium">{file.name}</p>
                        <p className="text-xs">{file.type} • {file.size}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
